import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';

/**
 * Cmd/Ctrl-Z routes to the AI revert while one is available. Document-level capture on purpose:
 * after Accept the focus sits in the sidebar, where the editor's own Mod-z keymap never fires -
 * and a plain editor undo would pop the AI step while the sidebar still shows it as accepted.
 */
const useAiEditRevertShortcut = (enabled: boolean, editor: Editor | undefined, onRevert: () => void) => {
  useEffect(() => {
    if (!enabled) return;

    const handleUndo = (event: KeyboardEvent) => {
      if (
        event.repeat ||
        event.key.toLowerCase() !== 'z' ||
        (!event.metaKey && !event.ctrlKey) ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target;
      const editorElement = editor?.view?.dom;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable && !editorElement?.contains(target))
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onRevert();
    };

    document.addEventListener('keydown', handleUndo, true);
    return () => document.removeEventListener('keydown', handleUndo, true);
  }, [enabled, editor, onRevert]);
};

export default useAiEditRevertShortcut;
